// Глобальный лоадер контентных картинок (общий для newgen и newgen_v2 —
// темы делят этот JS, различается только SCSS). Ванильный JS, без зависимостей.
//
// Конвенция для разметчика: добавить класс `.ng-img` на <img> — и всё.
// Лоадер сам:
//   1. штампует родителя картинки классом .ng-img-box (SCSS даёт ему
//      position:relative; при необходимости обёртку можно разметить и явно,
//      но ТОЛЬКО на прямом родителе <img> — CSS скрытия/фейда работает через
//      селектор прямого потомка `.ng-img-box... > .ng-img`);
//   2. на время загрузки вешает на обёртку .is-img-loading — подложка
//      в тоне темы + центрированное кольцо в стиле busy-конвенции
//      (см. ng-busy-spin / ngg-busy-spin), сама картинка скрыта;
//   3. по load снимает спиннер и вешает .is-img-loaded — плавный fade-in;
//   4. по error вешает .is-img-error — нейтральная подложка, битая иконка
//      браузера никогда не показывается;
//   5. уже закэшированные картинки (img.complete && naturalWidth > 0)
//      не трогает вовсе: ни спиннера, ни fade — никакого мигания; если же
//      complete, а размеров нет (загрузка упала ещё до скана либо SVG без
//      intrinsic-размеров) — дорешивает через decode(): битая → error-state,
//      валидная — без состояний;
//   6. через MutationObserver подхватывает <img>, отрисованные React'ом
//      после старта, и рестартует цикл при смене атрибута src (например,
//      перезагрузка аватара) — React-компонентам lifecycle-код не нужен.

const BOX = 'ng-img-box';
const LOADING = 'is-img-loading';
const LOADED = 'is-img-loaded';
const ERROR = 'is-img-error';
const SELECTOR = 'img.ng-img';

// Реестр URL, которые уже успешно показывали: пересозданный React'ом <img>
// с тем же src имеет complete=false, хотя картинка в кэше — без реестра
// такой ремоунт запускал бы полный цикл спиннер+fade на видимой картинке.
const loadedSrcs = new Set();

// Обёртка картинки: явно размеченный .ng-img-box (должен быть прямым
// родителем <img>) или родитель, которого штампуем этим классом
// (classList.add идемпотентен).
function boxFor(img) {
  const box = img.closest('.' + BOX) || img.parentElement;
  if (!box) return null;
  box.classList.add(BOX);
  return box;
}

// (Ре)старт цикла загрузки для одного <img>.
function start(img) {
  const box = boxFor(img);
  if (!box) return;
  if (!img.getAttribute('src')) {
    // Пустой src (например, иконки нет) — никаких состояний, ничего не крутим.
    box.classList.remove(LOADING, ERROR);
    return;
  }
  if (loadedSrcs.has(img.currentSrc || img.src)) {
    // Этот URL уже успешно показывали — ремоунт/повторная установка src:
    // ни спиннера, ни повторного fade. LOADED тоже снимаем: свежий узел
    // в старом боксе с is-img-loaded заново проиграл бы keyframes fade-in.
    box.classList.remove(LOADING, ERROR, LOADED);
    return;
  }
  if (img.complete) {
    // Загрузка уже завершилась до привязки обработчиков — load/error на
    // complete-картинке повторно не стрельнут, состояние решаем прямо здесь.
    if (img.naturalWidth > 0) {
      // Кэш или мгновенная загрузка (data: URI) — без спиннера и без fade.
      box.classList.remove(LOADING, ERROR, LOADED);
      return;
    }
    // complete без размеров — либо загрузка уже упала (404/блокировка до
    // скана; вечный спиннер тут недопустим), либо валидный SVG без
    // intrinsic-размеров (браузеры дают naturalWidth 0 и после успешного
    // load). decode() различает: для битой картинки — reject.
    if (typeof img.decode === 'function') {
      img.decode().then(() => {
        box.classList.remove(LOADING, ERROR);
      }, () => {
        box.classList.remove(LOADING, LOADED);
        box.classList.add(ERROR);
      });
    } else {
      box.classList.remove(LOADING, LOADED);
      box.classList.add(ERROR);
    }
    return;
  }
  if (img.naturalWidth > 0) {
    // Размеры уже известны — браузер прогрессивно отрисовывает картинку,
    // не гасим её спиннером (при реальной смене src naturalWidth сбросится
    // в 0, так что настоящая новая загрузка спиннер получит).
    box.classList.remove(LOADING, ERROR, LOADED);
    return;
  }
  box.classList.remove(LOADED, ERROR);
  box.classList.add(LOADING);
}

// Одноразовая привязка обработчиков к <img> (флаг живёт на самом узле,
// React чужие свойства DOM-узла не трогает).
function bind(img) {
  if (img.__ngImgLoadBound) return;
  img.__ngImgLoadBound = true;
  img.addEventListener('load', () => {
    const box = boxFor(img);
    if (!box) return;
    // Fade показываем только если реально ждали (кэшированные картинки
    // сюда не попадают — start() их не помечал).
    const waited = box.classList.contains(LOADING);
    box.classList.remove(LOADING, ERROR);
    loadedSrcs.add(img.currentSrc || img.src);
    if (waited) box.classList.add(LOADED);
  });
  img.addEventListener('error', () => {
    const box = boxFor(img);
    if (!box) return;
    box.classList.remove(LOADING, LOADED);
    box.classList.add(ERROR);
  });
  start(img);
}

// Инициализация узла и всех его потомков-картинок.
function scan(node) {
  if (!node || node.nodeType !== 1) return;
  if (node.matches(SELECTOR)) bind(node);
  const found = node.querySelectorAll(SELECTOR);
  for (let i = 0; i < found.length; i += 1) bind(found[i]);
}

export function initImgLoad() {
  scan(document.body);
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.type === 'childList') {
        m.addedNodes.forEach(scan);
      } else if (m.type === 'attributes'
          && m.target.nodeType === 1
          && m.target.matches(SELECTOR)) {
        // Установка src в то же самое значение (React ре-рендер пропа,
        // слайдеры) — не смена: рестарт цикла мигал бы видимой картинкой.
        if (m.oldValue === m.target.getAttribute('src')) return;
        // Смена src на уже смонтированном <img> (новый аватар и т.п.) —
        // рестартуем цикл: старая картинка прячется, кольцо до нового load.
        bind(m.target); // no-op, если уже привязан
        start(m.target);
      }
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ['src'],
  });
}



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/shared/imgLoad.js